/**
 * Builds a field name with optional prefix.
 *
 * @param {string} fieldName - The base field name
 * @param {string} [namePrefix] - Optional namespace prefix
 * @returns {string} The full field name with prefix if provided
 */
export function buildFieldName(fieldName, namePrefix) {
  return namePrefix ? `${namePrefix}.${fieldName}` : fieldName;
}

/**
 * Focuses on the first field with an error in a React Hook Form validation error object.
 *
 * Supports both nested errors (e.g., {section: {field: {message: '...'}}})
 * and flat errors (e.g., {field: {message: '...'}})
 *
 * @param {object} validationErrors - The validation errors object from React Hook Form
 * @param {object} [formMethods] - Optional form methods object from useForm hook for fallback focusing
 */
export function focusFirstErrorField(validationErrors, formMethods) {
  if (!validationErrors || typeof validationErrors !== 'object') {
    return;
  }

  const errorKeys = Object.keys(validationErrors);
  if (!errorKeys.length) {
    return;
  }

  // Get the first error entry to analyze
  const firstKey = errorKeys[0];
  const firstValue = validationErrors[firstKey];

  // Early return if firstValue is not an object
  if (!firstValue || typeof firstValue !== 'object') {
    return;
  }

  let fieldToFocus = '';
  const isFlatErrorStructure = 'type' in firstValue;

  if (isFlatErrorStructure) {
    fieldToFocus = firstKey;
  } else {
    const nestedKeys = Object.keys(firstValue);
    if (!nestedKeys.length) {
      return;
    }
    fieldToFocus = `${firstKey}.${nestedKeys[0]}`;
  }

  // Use setTimeout to ensure the DOM is ready
  setTimeout(() => {
    const element = document.querySelector(`[name="${fieldToFocus}"]`);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
    } else if (formMethods && typeof formMethods.setFocus === 'function') {
      // Only use setFocus as a fallback if formMethods is provided
      formMethods.setFocus(fieldToFocus, { shouldSelect: true });
    }
  }, 100);
}
